import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Download, FileText, FileSearch, History, Loader2, PackageCheck, RefreshCw, RotateCcw, Send, SlidersHorizontal, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiError } from '../api/client';
import { buscarXmlCompletoDFe, concluirDFe, consultarDFe, criarDevolucaoDFe, DFeDocumento, getDFeDocumento, importarCompraDFe, listDFeDocumentos, manifestarDFe, setDFeNsu } from '../api/dfe';
import FormField from '../components/FormField';
import LoadingSpinner from '../components/LoadingSpinner';
import { useEmitenteAtivo } from '../context/EmitenteAtivoContext';
import { formatCNPJ, formatDateTime, formatMoney } from '../utils/formatters';

const EVENTOS = [
  { value: '210210', label: 'Ciência da operação' },
  { value: '210200', label: 'Confirmar operação' },
  { value: '210220', label: 'Desconhecer operação' },
  { value: '210240', label: 'Operação não realizada' },
];

function permiteDevolucao(d: DFeDocumento) {
  if (typeof d.pode_devolucao === 'boolean') return d.pode_devolucao;
  return d.tem_xml && !(d.schema || '').toLowerCase().includes('resnfe');
}

function permiteDanfe(d: DFeDocumento) {
  if (typeof d.pode_danfe === 'boolean') return d.pode_danfe;
  return d.tem_xml && !!d.chave_acesso && !(d.schema || '').toLowerCase().includes('resnfe');
}

function eventoLabel(codigo?: string | null) {
  return EVENTOS.find((e) => e.value === codigo)?.label || codigo || '-';
}

function xmlLabel(d: DFeDocumento) {
  if (d.tem_xml_completo || d.xml_status === 'completo') return { label: 'XML completo', className: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300' };
  if (d.is_resumo || d.xml_status === 'resumo') return { label: 'Resumo SEFAZ', className: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300' };
  if (d.tem_xml) return { label: 'XML indisponível', className: 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300' };
  return { label: 'Sem XML', className: 'border-slate-200 bg-slate-100 text-slate-600 dark:border-neutral-500/30 dark:bg-neutral-500/10 dark:text-neutral-400' };
}

export default function DFePage() {
  const { emitenteId } = useEmitenteAtivo();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [busca, setBusca] = useState('');
  const [nsuManual, setNsuManual] = useState('');
  const [feedback, setFeedback] = useState<{ tipo: 'ok' | 'err'; msg: string } | null>(null);
  const [manifestando, setManifestando] = useState<DFeDocumento | null>(null);
  const [detalhando, setDetalhando] = useState<DFeDocumento | null>(null);
  const [tipoEvento, setTipoEvento] = useState('210210');
  const [justificativa, setJustificativa] = useState('');

  const q = useQuery({
    enabled: !!emitenteId,
    queryKey: ['dfe-documentos', emitenteId, busca],
    queryFn: () => listDFeDocumentos(emitenteId!, busca),
    refetchOnWindowFocus: false,
  });

  const docs = q.data?.data || [];
  const ultimoNsu = q.data?.meta?.ultimo_nsu || '0';

  const detalheQ = useQuery({
    enabled: !!emitenteId && !!detalhando,
    queryKey: ['dfe-documento', emitenteId, detalhando?.id],
    queryFn: () => getDFeDocumento(emitenteId!, detalhando!.id),
    refetchOnWindowFocus: false,
  });

  const contadores = useMemo(() => {
    return {
      total: docs.length,
      comXml: docs.filter((d) => d.tem_xml_completo || d.xml_status === 'completo').length,
      resumo: docs.filter((d) => d.is_resumo || d.xml_status === 'resumo').length,
      manifestados: docs.filter((d) => d.ultima_manifestacao).length,
      concluidos: docs.filter((d) => d.nota_concluida).length,
    };
  }, [docs]);

  const consultarMut = useMutation({
    mutationFn: () => consultarDFe(emitenteId!),
    onSuccess: (data) => {
      setFeedback({
        tipo: 'ok',
        msg: `Consulta concluída. Recebidos ${data.total_recebido}, novos ${data.documentos_novos}, último NSU ${data.ultimo_nsu}.`,
      });
      qc.invalidateQueries({ queryKey: ['dfe-documentos', emitenteId] });
    },
    onError: (e) => setFeedback({ tipo: 'err', msg: apiError(e, 'Falha na consulta DF-e') }),
  });

  const nsuMut = useMutation({
    mutationFn: () => setDFeNsu(emitenteId!, nsuManual),
    onSuccess: (data) => {
      setFeedback({ tipo: 'ok', msg: `NSU ajustado para ${data.ultimo_nsu}.` });
      setNsuManual('');
      qc.invalidateQueries({ queryKey: ['dfe-documentos', emitenteId] });
    },
    onError: (e) => setFeedback({ tipo: 'err', msg: apiError(e, 'Falha ao ajustar NSU') }),
  });

  const manifMut = useMutation({
    mutationFn: () => manifestarDFe(emitenteId!, manifestando!.id, tipoEvento, justificativa),
    onSuccess: (data) => {
      const xmlMsg = data.xml_baixado ? ' XML completo baixado.' : data.xml_mensagem ? ` ${data.xml_mensagem}` : '';
      setFeedback({ tipo: 'ok', msg: `Manifestação enviada: cStat ${data.codigo_status || '-'} - ${data.motivo_status || data.status || ''}.${xmlMsg}` });
      setManifestando(null);
      setJustificativa('');
      setTipoEvento('210210');
      qc.invalidateQueries({ queryKey: ['dfe-documentos', emitenteId] });
      qc.invalidateQueries({ queryKey: ['dfe-documento', emitenteId, manifestando?.id] });
    },
    onError: (e) => setFeedback({ tipo: 'err', msg: apiError(e, 'Falha ao manifestar documento') }),
  });

  const devolucaoMut = useMutation({
    mutationFn: (docId: number) => criarDevolucaoDFe(emitenteId!, docId),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['dfe-documentos', emitenteId] });
      navigate(`/documentos/nfe/${data.rascunho_id}`);
    },
    onError: (e) => setFeedback({ tipo: 'err', msg: apiError(e, 'Falha ao criar devolução') }),
  });

  const compraMut = useMutation({
    mutationFn: (docId: number) => importarCompraDFe(emitenteId!, docId),
    onSuccess: (data) => {
      setFeedback({ tipo: 'ok', msg: data.reutilizado ? `Compra #${data.compra_id} já existia.` : `Compra #${data.compra_id} importada.` });
      qc.invalidateQueries({ queryKey: ['dfe-documentos', emitenteId] });
      navigate('/compras');
    },
    onError: (e) => setFeedback({ tipo: 'err', msg: apiError(e, 'Falha ao importar compra') }),
  });

  const xmlCompletoMut = useMutation({
    mutationFn: (docId: number) => buscarXmlCompletoDFe(emitenteId!, docId),
    onSuccess: (data) => {
      setFeedback({ tipo: 'ok', msg: data.mensagem || 'XML completo baixado.' });
      qc.invalidateQueries({ queryKey: ['dfe-documentos', emitenteId] });
      if (detalhando) {
        qc.invalidateQueries({ queryKey: ['dfe-documento', emitenteId, detalhando.id] });
      }
    },
    onError: (e) => setFeedback({ tipo: 'err', msg: apiError(e, 'XML completo ainda não disponível') }),
  });

  const concluirMut = useMutation({
    mutationFn: (doc: DFeDocumento) => concluirDFe(emitenteId!, doc.id, !doc.nota_concluida),
    onSuccess: (data) => {
      setFeedback({ tipo: 'ok', msg: data.nota_concluida ? 'Documento marcado como concluído.' : 'Documento reaberto no histórico.' });
      qc.invalidateQueries({ queryKey: ['dfe-documentos', emitenteId] });
      qc.invalidateQueries({ queryKey: ['dfe-documento', emitenteId, data.id] });
    },
    onError: (e) => setFeedback({ tipo: 'err', msg: apiError(e, 'Falha ao atualizar histórico') }),
  });

  if (!emitenteId) {
    return <div className="card-surface p-6 text-center text-sm text-slate-600 dark:text-slate-400">Selecione um emitente para consultar DF-e.</div>;
  }

  return (
    <div className="space-y-5">
      <header className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/70 dark:shadow-none md:p-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="eyebrow">Manifestação do destinatário</div>
          <h1 className="h-display mt-1 text-2xl text-slate-950 md:text-3xl dark:text-slate-100">DF-e</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-400">
            Consulte documentos destinados ao CNPJ ativo, registre ciência/confirmação e baixe XML completo quando disponível.
          </p>
        </div>
        <button className="btn-primary" onClick={() => consultarMut.mutate()} disabled={consultarMut.isPending}>
          {consultarMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Consultar SEFAZ
        </button>
        </div>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-3">
        <div className="card-surface p-4">
          <div className="eyebrow">Documentos</div>
          <div className="mono mt-1 text-2xl text-slate-950 dark:text-slate-100">{contadores.total}</div>
        </div>
        <div className="card-surface p-4">
          <div className="eyebrow">XML completo</div>
          <div className="mono mt-1 text-2xl text-emerald-700 dark:text-emerald-300">{contadores.comXml}</div>
        </div>
        <div className="card-surface p-4">
          <div className="eyebrow">Só resumo</div>
          <div className="mono mt-1 text-2xl text-amber-700 dark:text-amber-300">{contadores.resumo}</div>
        </div>
        <div className="card-surface p-4">
          <div className="eyebrow">Manifestados</div>
          <div className="mono mt-1 text-2xl text-slate-950 dark:text-slate-100">{contadores.manifestados}</div>
        </div>
        <div className="card-surface p-4">
          <div className="eyebrow">Concluídos</div>
          <div className="mono mt-1 text-2xl text-slate-950 dark:text-slate-100">{contadores.concluidos}</div>
        </div>
        <div className="card-surface p-4">
          <div className="eyebrow">Último NSU</div>
          <div className="mono mt-1 text-2xl text-sky-700 dark:text-sky-300">{ultimoNsu}</div>
        </div>
      </section>

      <section className="card-surface p-4 space-y-3">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4 text-slate-400" />
          <h2 className="font-semibold text-slate-950 dark:text-slate-100">Filtros e NSU</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <FormField label="Buscar" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Chave, NSU, fornecedor" className="md:col-span-2" />
          <FormField label="Definir último NSU" value={nsuManual} onChange={(e) => setNsuManual(e.target.value.replace(/\D/g, ''))} />
          <div className="flex items-end">
            <button className="btn-secondary w-full" disabled={!nsuManual || nsuMut.isPending} onClick={() => nsuMut.mutate()}>
              {nsuMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Ajustar
            </button>
          </div>
        </div>
        {feedback && (
          <div className={`rounded-lg border px-3 py-2 text-sm ${feedback.tipo === 'ok' ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-300' : 'border-rose-200 bg-rose-50 text-rose-800 dark:bg-rose-500/10 dark:border-rose-500/30 dark:text-rose-300'}`}>
            {feedback.msg}
          </div>
        )}
      </section>

      <section className="card-surface overflow-hidden">
        {q.isLoading ? (
          <div className="grid place-items-center py-12"><LoadingSpinner /></div>
        ) : docs.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-500">
            <FileSearch className="h-8 w-8 mx-auto mb-2 text-slate-600" />
            Nenhum DF-e armazenado para o emitente ativo.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-100 text-left text-xs uppercase tracking-[0.08em] text-slate-500 dark:bg-slate-900/80">
                <tr>
                  <th className="px-4 py-3">Documento</th>
                  <th className="px-4 py-3">Fornecedor</th>
                  <th className="px-4 py-3">Valor</th>
                  <th className="px-4 py-3">Recebimento</th>
                  <th className="px-4 py-3">Manifestação</th>
                  <th className="px-4 py-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-white/5">
                {docs.map((d) => (
                  <tr key={d.id}>
                    <td className="px-4 py-3">
                      <div className="font-medium">NF-e {d.numero || '-'}</div>
                      <div className="text-xs text-neutral-500">NSU {d.nsu || '-'} Série {d.serie || '-'}</div>
                      <div className={`mt-1 inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${xmlLabel(d).className}`}>
                        {xmlLabel(d).label}
                      </div>
                      <div className="max-w-[320px] truncate text-xs text-neutral-500">{d.chave_acesso}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div>{d.emitente_nome || '-'}</div>
                      <div className="text-xs text-neutral-500">{d.emitente_cpf_cnpj ? formatCNPJ(d.emitente_cpf_cnpj) : '-'}</div>
                    </td>
                    <td className="px-4 py-3">{formatMoney(d.valor_nfe || 0)}</td>
                    <td className="px-4 py-3">{d.data_recebimento ? formatDateTime(d.data_recebimento) : '-'}</td>
                    <td className="px-4 py-3">
                      {d.ultima_manifestacao ? (
                        <div>
                          <div className="font-medium">{eventoLabel(d.ultima_manifestacao.tipo_evento)}</div>
                          <div className="text-xs text-neutral-500">cStat {d.ultima_manifestacao.codigo_status || '-'} · {d.manifestacoes_count || 1} evento(s)</div>
                          {d.rascunho_nfe_id && <div className="text-xs text-emerald-700 dark:text-emerald-400">Rascunho #{d.rascunho_nfe_id}</div>}
                          {d.compra_id && <div className="text-xs text-emerald-700 dark:text-emerald-400">Compra #{d.compra_id}</div>}
                          {d.nota_concluida && <div className="text-xs text-sky-700 dark:text-sky-400">Concluído</div>}
                        </div>
                      ) : (
                        <div>
                          <span className="text-neutral-500">Pendente</span>
                          {d.precisa_manifestacao && <div className="text-xs text-amber-700 dark:text-amber-400">Manifestar para liberar XML completo</div>}
                          {d.rascunho_nfe_id && <div className="text-xs text-emerald-700 dark:text-emerald-400">Rascunho #{d.rascunho_nfe_id}</div>}
                          {d.compra_id && <div className="text-xs text-emerald-700 dark:text-emerald-400">Compra #{d.compra_id}</div>}
                          {d.nota_concluida && <div className="text-xs text-sky-700 dark:text-sky-400">Concluído</div>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button className="btn-secondary" onClick={() => setDetalhando(d)}>
                          <History className="h-4 w-4" />
                          Histórico
                        </button>
                        <button className="btn-secondary" onClick={() => setManifestando(d)}>
                          <Send className="h-4 w-4" />
                          Manifestar
                        </button>
                        <button className="btn-secondary" disabled={!d.tem_xml} onClick={() => { window.location.href = `/api/dfe/documentos/${d.id}/xml?emitente_id=${emitenteId}`; }}>
                          <Download className="h-4 w-4" />
                          XML
                        </button>
                        <button className="btn-secondary" disabled={d.tem_xml_completo || xmlCompletoMut.isPending} onClick={() => xmlCompletoMut.mutate(d.id)}>
                          {xmlCompletoMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                          XML completo
                        </button>
                        <button className="btn-secondary" disabled={!permiteDanfe(d)} onClick={() => { window.location.href = `/api/dfe/documentos/${d.id}/danfe?emitente_id=${emitenteId}`; }}>
                          <FileText className="h-4 w-4" />
                          DANFE
                        </button>
                        <button className="btn-secondary" disabled={!permiteDevolucao(d) || devolucaoMut.isPending} onClick={() => devolucaoMut.mutate(d.id)}>
                          {devolucaoMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                          Devolução
                        </button>
                        <button className="btn-secondary" disabled={!d.pode_importar_compra || compraMut.isPending} onClick={() => compraMut.mutate(d.id)}>
                          {compraMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageCheck className="h-4 w-4" />}
                          Compra
                        </button>
                        <button className="btn-secondary" disabled={concluirMut.isPending} onClick={() => concluirMut.mutate(d)}>
                          {concluirMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                          {d.nota_concluida ? 'Reabrir' : 'Concluir'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {detalhando && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/45 p-4 backdrop-blur-sm dark:bg-black/60">
          <div className="card-surface w-full max-w-3xl max-h-[88vh] overflow-hidden">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-white/10">
              <div>
                <h2 className="text-lg font-semibold">Histórico DF-e {detalhando.numero || ''}</h2>
                <p className="text-xs text-neutral-500 break-all">{detalhando.chave_acesso}</p>
              </div>
              <button className="btn-secondary px-2" onClick={() => setDetalhando(null)} aria-label="Fechar histórico">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="max-h-[72vh] overflow-y-auto p-5 space-y-5">
              {detalheQ.isLoading ? (
                <div className="grid place-items-center py-10"><LoadingSpinner /></div>
              ) : detalheQ.isError ? (
                <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300">
                  {apiError(detalheQ.error, 'Falha ao carregar histórico DF-e')}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                      <div className="eyebrow">XML</div>
                      <div className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-xs ${xmlLabel(detalheQ.data || detalhando).className}`}>
                        {xmlLabel(detalheQ.data || detalhando).label}
                      </div>
                    </div>
                    <div>
                      <div className="eyebrow">Manifestações</div>
                      <div className="text-lg font-semibold">{detalheQ.data?.manifestacoes.length || 0}</div>
                    </div>
                    <div>
                      <div className="eyebrow">Status</div>
                      <div className="text-sm font-medium">{detalheQ.data?.status || detalhando.status}</div>
                    </div>
                    <div>
                      <div className="eyebrow">Recebimento</div>
                      <div className="text-sm font-medium">{(detalheQ.data?.data_recebimento || detalhando.data_recebimento) ? formatDateTime((detalheQ.data?.data_recebimento || detalhando.data_recebimento)!) : '-'}</div>
                    </div>
                  </div>

                  {(detalheQ.data?.precisa_manifestacao || detalhando.precisa_manifestacao) && (
                    <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>Este documento veio como resumo. Registre ciência/confirmação e consulte novamente para tentar obter o XML completo.</span>
                    </div>
                  )}

                  {!((detalheQ.data || detalhando).tem_xml_completo) && (
                    <div className="flex flex-col gap-2 rounded-lg border border-slate-200 px-3 py-3 md:flex-row md:items-center md:justify-between dark:border-white/10">
                      <div>
                        <div className="font-medium">Buscar XML completo</div>
                        <div className="text-xs text-neutral-500">Repete a consulta por chave na SEFAZ para liberar DANFE e devolução quando o resumo já foi manifestado.</div>
                      </div>
                      <button className="btn-secondary" disabled={xmlCompletoMut.isPending} onClick={() => xmlCompletoMut.mutate((detalheQ.data || detalhando).id)}>
                        {xmlCompletoMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        Buscar agora
                      </button>
                    </div>
                  )}

                  <div className="space-y-2">
                    <div className="eyebrow">Trilha</div>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                      {(detalheQ.data?.trilha || detalhando.trilha || []).map((step) => (
                        <div key={step.label} className={`rounded-lg border px-3 py-2 text-xs ${step.status === 'done' ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-300' : step.status === 'current' ? 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/25 dark:bg-sky-500/10 dark:text-sky-300' : 'border-slate-200 text-neutral-500 dark:border-white/10'}`}>
                          {step.label}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="eyebrow">Eventos</div>
                    {(detalheQ.data?.historico || []).length === 0 ? (
                      <div className="text-sm text-neutral-500">Nenhum evento registrado.</div>
                    ) : (
                      <div className="space-y-2">
                        {detalheQ.data!.historico.map((ev, idx) => (
                          <div key={`${ev.tipo}-${idx}`} className="rounded-lg border border-slate-200 px-3 py-2 dark:border-white/10">
                            <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                              <div className="font-medium">{ev.titulo}</div>
                              <div className="text-xs text-neutral-500">{ev.data ? formatDateTime(ev.data) : '-'}</div>
                            </div>
                            <div className="mt-1 text-xs text-neutral-500">
                              {ev.cstat ? `cStat ${ev.cstat}` : 'cStat -'} · protocolo {ev.protocolo || '-'} · {ev.motivo || '-'}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {manifestando && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/45 p-4 backdrop-blur-sm dark:bg-black/60">
          <div className="card-surface w-full max-w-lg p-5 space-y-4">
            <div>
              <h2 className="text-lg font-semibold">Manifestar NF-e {manifestando.numero || ''}</h2>
              <p className="text-xs text-neutral-500 break-all">{manifestando.chave_acesso}</p>
            </div>
            <label className="space-y-1 text-sm block">
              <span className="font-medium text-neutral-700 dark:text-neutral-300">Evento</span>
              <select className="field-input" value={tipoEvento} onChange={(e) => setTipoEvento(e.target.value)}>
                {EVENTOS.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
              </select>
            </label>
            {tipoEvento === '210240' && (
              <FormField label="Justificativa" value={justificativa} onChange={(e) => setJustificativa(e.target.value)} />
            )}
            <div className="flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setManifestando(null)}>Cancelar</button>
              <button className="btn-primary" disabled={manifMut.isPending || (tipoEvento === '210240' && justificativa.trim().length < 15)} onClick={() => manifMut.mutate()}>
                {manifMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Enviar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
